'use client';
import { track as vercelTrack } from '@vercel/analytics';
import type React from 'react';
import { createContext, PropsWithChildren, useCallback, useContext, useMemo } from 'react';

type TrackFunction = (event: string, data?: Record<string, unknown>) => void;

type AnalyticsContext = {
    track: TrackFunction;
};
const AnalyticsContext = createContext<AnalyticsContext>(null);

const AnalyticsProvider: React.FC<
    PropsWithChildren<{
        overallData: Record<string, string>;
        eventPrefix?: string;
        trackers?: TrackFunction[];
    }>
> = ({ children, overallData, eventPrefix, trackers = [] }) => {
    const allTrackers = useMemo<TrackFunction[]>(
        () => [(event, data) => vercelTrack(event, data as Record<string, string>), ...trackers],
        [trackers]
    );

    const track = useCallback(
        (event: string, data: Record<string, string> = {}) => {
            const prefixedEvent = eventPrefix ? `${eventPrefix}: ${event}` : event;
            const mergedData = { ...overallData, ...data };

            for (const t of allTrackers) {
                t(prefixedEvent, mergedData);
            }
        },
        [allTrackers, overallData, eventPrefix]
    );

    const context = useMemo(() => ({ track }), [track]);
    return <AnalyticsContext.Provider value={context}>{children}</AnalyticsContext.Provider>;
};

export const useAnalytics = () => {
    const context = useContext(AnalyticsContext);
    if (!context) {
        // fallback to vercel track, no crashing if no provider is configured
        return {
            track: vercelTrack
        };
    }
    return context;
};

export default AnalyticsProvider;
